import { Botao, BotaoIcone, EtiquetaStatus, type CorStatus } from '@persona/ui';
import { ArrowRight, Pencil, X } from 'lucide-react';
import type { ReactNode } from 'react';
import type { CampoUi, EsquemaRotina } from '../dicionario/tipos';
import { exibir } from './formatacao';

type Linha = Record<string, unknown> & { id: number };

interface PainelRegistroProps {
  esquema: EsquemaRotina;
  /** Campos que a lista trouxe: o painel não busca nada além do que já está na linha. */
  campos: CampoUi[];
  linha: Linha | undefined;
  titulo: string;
  subtitulo?: string;
  status?: { cor: CorStatus; rotulo: string } | null;
  descricoes: Record<string, Map<unknown, string>>;
  aoAbrir?: () => void;
  aoAlterar?: () => void;
  aoFechar: () => void;
}

function iniciais(texto: string) {
  return texto.replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
}

/** Painel de inspeção à direita da lista (ADR-0006): resumo da linha em foco, agrupado pelas pastas do dicionário. */
export function PainelRegistro({ esquema, campos, linha, titulo, subtitulo, status, descricoes, aoAbrir, aoAlterar, aoFechar }: PainelRegistroProps) {
  const secoes = esquema.pastas
    .map((p) => ({ titulo: p.titulo, campos: campos.filter((c) => c.pasta === p.codigo) }))
    .concat([{ titulo: 'Outros dados', campos: campos.filter((c) => !esquema.pastas.some((p) => p.codigo === c.pasta)) }])
    .filter((s) => s.campos.length > 0);

  let corpo: ReactNode;
  if (!linha) {
    corpo = <p className="painel-registro-vazio">Escolha uma linha para ver os detalhes.</p>;
  } else {
    corpo = secoes.map((s) => (
      <section key={s.titulo} className="painel-registro-secao" aria-label={s.titulo}>
        <h3 className="painel-registro-secao-titulo">{s.titulo}</h3>
        <dl>
          {s.campos.map((c) => {
            const valor = exibir(c, linha[c.nome], descricoes[c.nome]);
            return (
              <div key={c.nome} className="painel-registro-campo">
                <dt>{c.titulo}</dt>
                <dd className={c.mono ? 'mono' : undefined}>{valor === '' ? <span className="texto-3">—</span> : valor}</dd>
              </div>
            );
          })}
        </dl>
      </section>
    ));
  }

  return (
    <aside className="painel-registro" aria-label={`Detalhes de ${esquema.titulo.toLowerCase()}`}>
      <header className="painel-registro-cabecalho">
        {linha ? (
          <div className="painel-registro-identidade">
            <span className="painel-registro-avatar" aria-hidden="true">{iniciais(titulo)}</span>
            <div className="painel-registro-nome">
              <h2>{titulo}</h2>
              <span className="painel-registro-meta">
                {subtitulo && <span className="texto-3">{subtitulo}</span>}
                {status && <EtiquetaStatus cor={status.cor} rotulo={status.rotulo} />}
              </span>
            </div>
          </div>
        ) : <h2>Detalhes</h2>}
        <BotaoIcone rotulo="Fechar detalhes" icone={<X size={16} strokeWidth={1.75} />} variante="fantasma" onClick={aoFechar} />
      </header>
      {linha && (aoAbrir || aoAlterar) && (
        <div className="painel-registro-acoes">
          {aoAlterar && <Botao tamanho="p" icone={<Pencil size={14} strokeWidth={1.75} aria-hidden="true" />} onClick={aoAlterar} atalho="F4">Alterar</Botao>}
          {aoAbrir && <Botao tamanho="p" onClick={aoAbrir}>Abrir cadastro <ArrowRight size={14} aria-hidden="true" /></Botao>}
        </div>
      )}
      <div className="painel-registro-corpo" tabIndex={0} role="region" aria-label="Resumo do registro">{corpo}</div>
    </aside>
  );
}
